import { useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useParams } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonSize,
  IconExport,
  Table,
  PermissionEnum,
  Popup,
  DateInput,
  BaseTransaction,
  ResponseError,
  BaseellaStatus,
  ButtonPseudo,
  useQueryState,
  useGetList,
  SchemaFilterType,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useUpdateOutgoingTransactionMutation } from '../outgoing/store/services';
import { useChangeTransactionStatusMutation } from '../../store/statuses/services';
import StatusesUrl from '../../types/statuses-url';
import { onSelectedRows } from './store/slices/list';
import { columns, columnsLabels, schema } from './variables/list';
import formatExportList from './helpers/format-export-list';
import { ListTypes } from './types';

import cn from './styles.module.css';

const TransactionList = ({
  isCreate = false,
  onSubmit,
  isLoading = true,
  requestInProgress,
  batchError,
  createdBatchId,
  getExportBatchesFile,
  getBatchById,
  getTransactionsList,
}: ListTypes): React.JSX.Element => {
  const { bankId, accountId, batchId } = useParams();

  const {
    export: {
      banks: { banks },
      list: {
        selectedTransactionIds,
        transactions,
        selectedTransactions,
        transactionsPagination,
      },
      batches: { accountNumber },
    },
    transactionsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();

  const [popupVisible, setPopupVisible] = useState(false);
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);
  const [value, setValue] = useState<string | undefined>();
  const [changeStatusErrors, setChangeStatusErrors] = useState<string[]>([]);

  const [updateTransaction] = useUpdateOutgoingTransactionMutation();
  const [changeStatus] = useChangeTransactionStatusMutation();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const { getListAction, setPage, errorList } = useGetList<BaseTransaction>({
    getList: getBatchById,
    itemId: batchId,
    onFilterChange,
    onSortChange,
    schema,
  });

  const {
    getListAction: getOtgoingListAction,
    setPage: setOutgoingListPage,
    errorList: outgoingListError,
  } = useGetList<BaseTransaction>({
    getList: getTransactionsList,
    onFilterChange,
    onSortChange,
    schema,
  });

  const formattedData = useMemo(() => {
    const data = formatExportList({
      list: transactions,
      bankId: bankId as string,
      accountId: accountId as string,
      isCreate,
      batchId: batchId as string,
    });

    return data;
  }, [transactions]);

  const onClosePopup = () => {
    setPopupVisible(false);
    setValue(undefined);
  };

  const onRowsSelect = (rows: string[]) => {
    if (
      !isEqual(rows, selectedTransactionIds) &&
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_EXPORT_CREATE_ACTION,
      )
    ) {
      return dispatch(onSelectedRows(rows));
    }

    return undefined;
  };

  const handleSetDate = async () => {
    setIsLoadingTransaction(true);
    setChangeStatusErrors([]);

    const filteredSelectedTransactions =
      selectedTransactions?.filter(
        (item) => item.baseellaStatus === BaseellaStatus.SENT,
      ) || [];

    if (
      filteredSelectedTransactions &&
      filteredSelectedTransactions.length > 0 &&
      value
    ) {
      const updateResponse = await Promise.all(
        filteredSelectedTransactions.map(async (item) => {
          const transactionResponse = (await updateTransaction({
            body: {
              paymentType: item.paymentType,
              chargeType: item.chargeType,
              pspPartnerId: item.pspPartnerId,
              pspPartnerAccountNumber: item.pspPartnerAccountNumber,
              documentNumber: item.documentNumber,
              info: item.info,
              acceptedOut: value,
            },
            id: item.id,
          })) as {
            data?: BaseTransaction;
            error: ResponseError;
          };

          return transactionResponse;
        }),
      );

      if (!updateResponse.find((item) => item.error)) {
        const changeStatusResponse = await Promise.all(
          filteredSelectedTransactions.map(async (item) => {
            const response = (await changeStatus({
              url: StatusesUrl.OUTGOING,
              id: item.id,
              body: {
                baseellaStatusFrom: item?.baseellaStatus,
                baseellaStatusTo: BaseellaStatus.ARCH,
              },
            })) as {
              error: ResponseError;
            };

            return response;
          }),
        );

        if (!changeStatusResponse.find((item) => item.error)) {
          setIsLoadingTransaction(false);
          onClosePopup();
        } else {
          const errors: string[] = [];

          changeStatusResponse?.forEach((item) => {
            const errorsText =
              item?.error?.data?.message[0] &&
              // @ts-ignore
              item?.error?.data?.message[0].constraints &&
              // @ts-ignore
              Object.values(item?.error?.data?.message[0].constraints)[0];

            errors.push(
              errorsText || 'Something went wrong. Internal server error',
            );
          });

          setChangeStatusErrors(errors);
        }
      }
    }
  };

  useEffect(() => {
    if (batchId && getBatchById) {
      getListAction({
        filters: {
          take: {
            value: ['30'],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [batchId, getBatchById]);

  useEffect(() => {
    if (bankId && accountNumber && getTransactionsList) {
      getOtgoingListAction({
        filters: {
          take: {
            value: ['30'],
            type: SchemaFilterType.STRING,
          },
          baseellaStatus: {
            value: [BaseellaStatus.READY_TO_SEND],
            type: SchemaFilterType.STRING,
          },
          pspPartnerId: {
            value: [bankId],
            type: SchemaFilterType.STRING,
          },
          pspPartnerAccountNumber: {
            value: [accountNumber],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [bankId, accountNumber, getTransactionsList]);

  return (
    <>
      <Popup
        title="Set accepted Out date"
        onCancelClick={onClosePopup}
        isVisible={popupVisible}
        isLoading={isLoadingTransaction}
        parent={document.getElementById('arch-popup') as HTMLElement}
        popupClassName={cn.acceptedDatePopup}
      >
        <>
          <DateInput
            placeholder="Select Date"
            onChange={(date) => setValue(date.toISOString())}
            value={value}
            disabled={isLoadingTransaction}
            withoutCustomSelector
            className={cn.dateInput}
          />
          {changeStatusErrors && changeStatusErrors?.length > 0 && (
            <ul className={cn.errorsList}>
              {changeStatusErrors.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          )}
          <div className={cn.popupControls}>
            <ButtonPseudo text="Cancel" onClick={onClosePopup} bold />
            <ButtonPrimary
              text={isLoadingTransaction ? 'Loading...' : 'Set'}
              onClick={handleSetDate}
              disabled={!value}
              bold
            />
          </div>
        </>
      </Popup>
      <Table
        tableFormClassName={cn.exportSidepanelTable}
        filters={filters}
        onFilterChange={() => {}}
        sort={sort}
        onSort={() => {}}
        onRowsSelect={onRowsSelect}
        columns={columns}
        data={formattedData || []}
        columnsLabels={columnsLabels}
        isLoading={isLoading}
        take="30"
        setPage={(data) =>
          getBatchById
            ? setPage({ ...data, filters })
            : setOutgoingListPage({
                ...data,
                filters: {
                  baseellaStatus: {
                    value: [BaseellaStatus.READY_TO_SEND],
                    type: SchemaFilterType.STRING,
                  },
                  pspPartnerId: {
                    value: [bankId as string],
                    type: SchemaFilterType.STRING,
                  },
                  pspPartnerAccountNumber: {
                    value: [accountNumber as string],
                    type: SchemaFilterType.STRING,
                  },
                },
              })
        }
        error={errorList || outgoingListError}
        pagination={transactionsPagination}
        disabledRowIds={transactions
          ?.filter(
            (item) =>
              item.baseellaStatus !== BaseellaStatus.READY_TO_SEND &&
              item.baseellaStatus !== BaseellaStatus.SENT,
          )
          ?.map((item) => item.id)}
        topContent={
          <>
            <div className={cn.buttonsWrapper}>
              {isCreate &&
              !createdBatchId &&
              activePermissions?.includes(
                PermissionEnum.TRANSACTIONS_EXPORT_CREATE_ACTION,
              ) &&
              (selectedTransactionIds?.length || formattedData?.length) ? (
                <ButtonPrimary
                  text={`Export ${
                    selectedTransactionIds?.length || formattedData?.length
                  } transaction`}
                  size={ButtonSize.SMALL}
                  icon={<IconExport />}
                  onClick={onSubmit}
                  disabled={
                    requestInProgress || !selectedTransactionIds?.length
                  }
                />
              ) : undefined}

              {!isCreate &&
              activePermissions?.includes(
                PermissionEnum.TRANSACTIONS_OUTGOING_EDIT_ACTION,
              ) ? (
                <ButtonPrimary
                  text="Set ARCH status"
                  size={ButtonSize.SMALL}
                  onClick={() => setPopupVisible(true)}
                  className={cn.setArchStatusButton}
                  disabled={
                    requestInProgress ||
                    !selectedTransactionIds?.length ||
                    !selectedTransactions?.filter(
                      (item) => item.baseellaStatus === BaseellaStatus.SENT,
                    )?.length ||
                    !formattedData?.length
                  }
                />
              ) : undefined}
              {createdBatchId && (
                <ButtonPrimary
                  text="Export"
                  size={ButtonSize.SMALL}
                  icon={<IconExport />}
                  onClick={() => getExportBatchesFile(createdBatchId)}
                  disabled={
                    requestInProgress ||
                    !banks?.find((item) => item.id === bankId)?.exportFileType
                  }
                />
              )}
            </div>
            {batchError && (
              <div className={cn.batchDownloadError}>{batchError}</div>
            )}
          </>
        }
        clickable
        withSaveSelectedRows
      />
    </>
  );
};

export default TransactionList;
